/**
 * Property data service for tax and insurance estimates
 * Uses national averages with ability to override
 */

// National average property tax rates by state (as percentage of home value)
const PROPERTY_TAX_RATES = {
    // Top states by property tax rate
    'NJ': 2.49, 'IL': 2.27, 'NH': 2.18, 'CT': 2.14, 'VT': 1.90,
    'WI': 1.85, 'TX': 1.80, 'NE': 1.76, 'OH': 1.56, 'RI': 1.53,
    'MI': 1.54, 'KS': 1.41, 'PA': 1.36, 'IA': 1.29, 'SD': 1.28,
    'NY': 1.23, 'ME': 1.20, 'MN': 1.12, 'MA': 1.04, 'AK': 1.04,
    'FL': 0.98, 'ND': 0.98, 'MD': 0.96, 'OR': 0.90, 'WA': 0.88,
    'IN': 0.85, 'GA': 0.83, 'NC': 0.78, 'MT': 0.74, 'ID': 0.72,
    'CA': 0.71, 'MO': 0.91, 'OK': 0.88, 'NM': 0.80, 'AZ': 0.66,
    'VA': 0.80, 'KY': 0.85, 'MS': 0.79, 'TN': 0.71, 'NV': 0.69,
    'AR': 0.62, 'UT': 0.60, 'WY': 0.58, 'CO': 0.55, 'SC': 0.55,
    'WV': 0.58, 'AL': 0.42, 'LA': 0.55, 'DE': 0.57, 'DC': 0.56,
    'HI': 0.28
};

// National average: 1.1% of home value annually
const DEFAULT_PROPERTY_TAX_RATE = 1.1;

// Homeowner insurance average costs by state (annual)
const HOMEOWNER_INSURANCE_RATES = {
    'OK': 5317, 'NE': 4631, 'KS': 4427, 'CO': 4290, 'TX': 4211,
    'LA': 4056, 'MS': 3784, 'AL': 3547, 'AR': 3298, 'SD': 3219,
    'MO': 3196, 'MT': 2987, 'WY': 2910, 'ND': 2798, 'GA': 2697,
    'FL': 2674, 'AZ': 2524, 'TN': 2510, 'SC': 2428, 'KY': 2394,
    'NM': 2341, 'IA': 2294, 'MN': 2258, 'NC': 2169, 'IN': 2098,
    'WI': 2031, 'IL': 1995, 'OH': 1974, 'MI': 1951, 'WV': 1897,
    'VA': 1867, 'AK': 1820, 'ME': 1735, 'CT': 1723, 'NV': 1689,
    'MD': 1678, 'PA': 1656, 'CA': 1638, 'WA': 1620, 'MA': 1615,
    'NH': 1577, 'NJ': 1544, 'RI': 1527, 'VT': 1490, 'NY': 1489,
    'ID': 1450, 'OR': 1388, 'DE': 1337, 'UT': 1233, 'DC': 1211,
    'HI': 1000
};

// National average: $1,820 annually
const DEFAULT_INSURANCE_ANNUAL = 1820;

/**
 * Get state code from zip code (simplified mapping)
 * In production, this would use a comprehensive zip-to-state database
 */
export function getStateFromZip(zipCode) {
    if (!zipCode) return null;
    const zipStr = String(zipCode);
    if (zipStr.length < 5) return null;

    const zip = parseInt(zipStr.substring(0, 3));

    // Simplified zip code to state mapping (first 3 digits)
    const zipRanges = {
        '005-009': 'NY', '010-027': 'MA', '028-029': 'RI', '030-038': 'NH',
        '039-049': 'ME', '050-059': 'VT', '060-069': 'CT', '070-089': 'NJ',
        '090-149': 'NY', '150-196': 'PA', '197-199': 'DE', '200-205': 'DC',
        '206-219': 'MD', '220-246': 'VA', '247-251': 'WV', '252-269': 'NC',
        '270-289': 'SC', '290-319': 'GA', '320-349': 'FL', '350-369': 'AL',
        '370-385': 'TN', '386-397': 'MS', '398-399': 'GA', '400-427': 'KY',
        '430-458': 'OH', '459-479': 'IN', '480-499': 'MI', '500-528': 'IA',
        '530-549': 'WI', '550-567': 'MN', '570-577': 'SD', '580-588': 'ND',
        '590-599': 'MT', '600-629': 'IL', '630-658': 'MO', '660-679': 'KS',
        '680-699': 'NE', '700-729': 'LA', '730-749': 'AR', '750-799': 'TX',
        '800-816': 'CO', '820-831': 'WY', '832-838': 'ID', '840-847': 'UT',
        '850-865': 'AZ', '870-884': 'NM', '885-898': 'TX', '890-899': 'NV',
        '900-961': 'CA', '970-979': 'OR', '980-994': 'WA', '995-999': 'AK',
        '967-968': 'HI'
    };

    for (const [range, state] of Object.entries(zipRanges)) {
        const [min, max] = range.split('-').map(n => parseInt(n));
        if (zip >= min && zip <= max) {
            return state;
        }
    }

    return null;
}

/**
 * Get property tax rate for a zip code
 * @param {string} zipCode - 5-digit zip code
 * @returns {object} Property tax information
 */
export function getPropertyTaxRate(zipCode) {
    const state = getStateFromZip(zipCode);
    const rate = state && PROPERTY_TAX_RATES[state]
        ? PROPERTY_TAX_RATES[state]
        : DEFAULT_PROPERTY_TAX_RATE;

    return {
        rate,
        state: state || 'Unknown',
        isEstimate: !state,
        source: state ? `${state} state average` : 'National average'
    };
}

/**
 * Calculate monthly property tax
 * @param {number} homePrice - Home price
 * @param {string} zipCode - Zip code
 * @returns {object} Property tax details
 */
export function calculatePropertyTax(homePrice, zipCode) {
    const taxInfo = getPropertyTaxRate(zipCode);
    const annualTax = homePrice * (taxInfo.rate / 100);
    const monthlyTax = annualTax / 12;

    return {
        monthly: monthlyTax,
        annual: annualTax,
        rate: taxInfo.rate,
        state: taxInfo.state,
        isEstimate: taxInfo.isEstimate,
        source: taxInfo.source
    };
}

/**
 * Get homeowner insurance estimate for a zip code
 * @param {string} zipCode - 5-digit zip code
 * @param {number} homePrice - Home price (for scaling)
 * @returns {object} Insurance information
 */
export function getHomeownerInsurance(zipCode, homePrice) {
    const state = getStateFromZip(zipCode);
    let annualCost = state && HOMEOWNER_INSURANCE_RATES[state]
        ? HOMEOWNER_INSURANCE_RATES[state]
        : DEFAULT_INSURANCE_ANNUAL;

    // Scale based on home price (baseline is $300k home)
    const baselinePrice = 300000;
    const scaleFactor = homePrice / baselinePrice;
    annualCost = annualCost * Math.sqrt(scaleFactor); // Square root for diminishing returns

    return {
        monthly: annualCost / 12,
        annual: annualCost,
        state: state || 'Unknown',
        isEstimate: true,
        source: state ? `${state} state average (scaled for home value)` : 'National average (scaled for home value)'
    };
}

/**
 * Calculate total monthly escrow (taxes + insurance + PMI)
 * @param {number} homePrice - Home price
 * @param {string} zipCode - Zip code
 * @param {number} loanAmount - Loan amount
 * @param {number} downPaymentPercent - Down payment percentage
 * @returns {object} Escrow breakdown
 */
export function calculateEscrow(homePrice, zipCode, loanAmount, downPaymentPercent) {
    const propertyTax = calculatePropertyTax(homePrice, zipCode);
    const insurance = getHomeownerInsurance(zipCode, homePrice);

    // Import PMI calculation from mortgageCalculations
    // For now, calculate inline
    const pmi = downPaymentPercent < 20
        ? (loanAmount * 0.0075) / 12
        : 0;

    const total = propertyTax.monthly + insurance.monthly + pmi;

    return {
        propertyTax: propertyTax.monthly,
        propertyTaxDetails: propertyTax,
        insurance: insurance.monthly,
        insuranceDetails: insurance,
        pmi,
        total,
        breakdown: {
            'Property Tax': propertyTax.monthly,
            'Homeowner Insurance': insurance.monthly,
            'PMI': pmi
        }
    };
}
